import { XIcon } from "@heroicons/react/solid";
import { Fragment } from "react";
import { Dialog, Transition } from "@headlessui/react";
import { Kbd } from "@mantine/core";
import Modal from "../../../components/Modal";
import Button from "../../../components/Button";

export default function GVKeyboardModal({ open, onCancel }) {
    const shortcuts = {
        ArrowDown: "Next Bid Page",
        ArrowUp: "Previous Bid Page",
        Comma: "Previous Tract Map",
        Period: "Next Tract Map",
        Space: "Play/Pause",
        "Ctrl+1": "Show 1st Completion",
        "Ctrl+2": "Show 2nd Completion",
        "Ctrl+3": "Show 3rd Completion",
        "Ctrl+4": "Show 4th Completion",
        "Ctrl+5": "Show 5th Completion",
        "Ctrl+6": "Show 6th Completion",
        "Ctrl+7": "Show 7th Completion",
        "Ctrl+8": "Show 8th Completion",
        "Ctrl+9": "Show 9th Completion",
        "Ctrl+0": "Show 10th Completion",
        "=": "Add 1 Second to Transition Duration",
        "-": "Subtract 1 Second to Transition Duration",
        NumpadAdd: "Add 1 Second to Transition Duration",
        NumpadSubtract: "Subtract 1 Second to Transition Duration",
    };

    // Helper function to render keyboard shortcuts with proper styling
    const renderKeyboardShortcut = (key) => {
        // Handle special arrow key names
        const keyMap = {
            ArrowRight: "→",
            ArrowLeft: "←",
            ArrowDown: "↓",
            ArrowUp: "↑",
            Space: "Space",
            Comma: ",",
            Period: ".",
            NumpadAdd: "Num +",
            NumpadSubtract: "Num -",
        };

        // Check if it's a combination (contains +)
        if (key.includes("+")) {
            const parts = key.split("+");
            return (
                <div className="flex items-center gap-1">
                    {parts.map((part, index) => (
                        <Fragment key={index}>
                            <Kbd>{keyMap[part] || part}</Kbd>
                            {index < parts.length - 1 && (
                                <span className="text-gray-400">+</span>
                            )}
                        </Fragment>
                    ))}
                </div>
            );
        }

        // Single key
        return <Kbd>{keyMap[key] || key}</Kbd>;
    };

    return (
        <Modal isOpen={open}>
            <div className="min-h-screen px-4 text-center">
                <Transition.Child
                    as={Fragment}
                    enter="ease-out duration-300"
                    enterFrom="opacity-0"
                    enterTo="opacity-100"
                    leave="ease-in duration-200"
                    leaveFrom="opacity-100"
                    leaveTo="opacity-0"
                >
                    <Dialog.Overlay className="fixed inset-0" />
                </Transition.Child>

                {/* This element is to trick the browser into centering the modal contents. */}
                <span
                    className="inline-block h-screen align-middle"
                    aria-hidden="true"
                >
                    &#8203;
                </span>
                <Transition.Child
                    as={Fragment}
                    enter="ease-out duration-300"
                    enterFrom="opacity-0 scale-95"
                    enterTo="opacity-100 scale-100"
                    leave="ease-in duration-200"
                    leaveFrom="opacity-100 scale-100"
                    leaveTo="opacity-0 scale-95"
                >
                    <div className="inline-block w-full max-w-xl p-6 my-8 overflow-hidden text-left align-middle transition-all transform bg-white shadow-xl">
                        <XIcon
                            className="float-right w-6 h-6 ml-1 cursor-pointer"
                            onClick={onCancel}
                        />

                        <Dialog.Title
                            as="h3"
                            className="text-xl font-medium leading-6 text-gray-900"
                        >
                            Keyboard Shortcuts
                        </Dialog.Title>

                        <hr className="my-4" />

                        <div className="space-y-3">
                            {Object.keys(shortcuts).map((key) => (
                                <div
                                    key={key}
                                    className="flex items-center justify-between"
                                >
                                    <div className="flex items-center min-w-0 flex-1">
                                        {renderKeyboardShortcut(key)}
                                    </div>
                                    <span className="ml-4 text-gray-700 flex-1">
                                        {shortcuts[key]}
                                    </span>
                                </div>
                            ))}
                        </div>

                        <div className="flex items-center mt-10 text-cta">
                            <div className="w-1/2 text-left"></div>
                            <div className="w-1/2 text-right">
                                <Button
                                    type={"secondary"}
                                    onClick={() => onCancel()}
                                    rounded
                                >
                                    Close
                                </Button>
                            </div>
                        </div>
                    </div>
                </Transition.Child>
            </div>
        </Modal>
    );
}
